import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Box, Typography, IconButton } from "@mui/material";
import { IconChevronLeft, IconPackages, IconBuildingStore, IconTruckDelivery } from "@tabler/icons-react";
import MobileMenuButton from "../components/MobileMenuButton";
import { assembliesApi, ozonAssembliesApi, ymAssembliesApi } from "../api";
import { barIconSx, ds, menuBadgeSx, shellSx, topBarSx } from "../theme/keeperTheme";

/** Счётчик заданий справа в строке меню: плашка-заглушка, пока грузим; ничего — если не удалось. */
function CountBadge({ value }) {
  if (value === undefined) {
    return (
      <Box
        sx={{
          minWidth: 40,
          height: 30,
          borderRadius: 999,
          bgcolor: ds.surface3,
          border: `1px solid ${ds.border}`,
        }}
      />
    );
  }
  if (value === null) return null;
  return (
    <Box sx={menuBadgeSx}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.875rem",
          color: ds.surface,
          lineHeight: 1.2,
          fontFeatureSettings: '"tnum"',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/** Вход в сборку: с какого маркетплейса начать. */
export default function MarketplacePickPage() {
  const { registerToolbarRefresh } = useOutletContext() || {};
  const navigate = useNavigate();
  const [wbCount, setWbCount] = useState(undefined);
  const [ozonCount, setOzonCount] = useState(undefined);
  const [ymCount, setYmCount] = useState(undefined);

  const loadCounts = useCallback(() => {
    const wb = assembliesApi
      .summary()
      .then((data) =>
        setWbCount(typeof data.assembly_orders_count === "number" ? data.assembly_orders_count : 0),
      )
      .catch(() => setWbCount(null));
    const ozon = ozonAssembliesApi
      .summary()
      .then((data) =>
        setOzonCount(typeof data.postings_count === "number" ? data.postings_count : 0),
      )
      .catch(() => setOzonCount(null));
    const ym = ymAssembliesApi
      .summary()
      .then((data) => setYmCount(typeof data.orders_count === "number" ? data.orders_count : 0))
      .catch(() => setYmCount(null));
    return Promise.all([wb, ozon, ym]);
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    if (!registerToolbarRefresh) return undefined;
    return registerToolbarRefresh(() => loadCounts());
  }, [registerToolbarRefresh, loadCounts]);

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          ...topBarSx,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
        }}
      >
        <IconButton onClick={() => navigate("/")} aria-label="Назад в меню" sx={barIconSx}>
          <IconChevronLeft size={26} stroke={1.5} />
        </IconButton>
        <Typography sx={{ color: ds.text, fontWeight: 700, flex: 1 }}>
          С чего начать сборку
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 3,
          pb: 4,
          px: 2,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <MobileMenuButton
            to="/assemblies"
            label="Wildberries"
            hint="Сборочные задания, скан QR стикера"
            icon={<IconPackages size={26} stroke={1.5} />}
            trailing={<CountBadge value={wbCount} />}
          />
          <MobileMenuButton
            to="/ozon/assemblies"
            label="Ozon"
            hint="Отправления FBS, скан штрихкода отправления"
            tone="info"
            icon={<IconBuildingStore size={26} stroke={1.5} />}
            trailing={<CountBadge value={ozonCount} />}
          />
          <MobileMenuButton
            to="/ym/assemblies"
            label="Яндекс Маркет"
            hint="Заказы FBS, скан КИЗ"
            icon={<IconTruckDelivery size={26} stroke={1.5} />}
            trailing={<CountBadge value={ymCount} />}
          />
        </Box>
      </Box>
    </Box>
  );
}
